import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import HeroSection from './HeroSection';
import VideoSection from './VideoSection';
import AchievementsSection from './AchievementsSection';
import FrameworkSection from './FrameworkSection';
import CreditControlSystemSection from './CreditControlSystemSection';
import ClientAssessmentSection from './ClientAssessmentSection';
import CashFlowSection from './CashFlowSection';
import CoachingPlanSection from './CoachingPlanSection';
import ResultsSection from './ResultsSection';
import VideoTestimonialsSection from './VideoTestimonialsSection';
import TestimonialsSection from '../BlockedPayments/TestimonialsSection';
import ContactSection from './ContactSection';
import FooterSection from './FooterSection';
import PaymentModal from './PaymentModal';
import RequestFormModal from './RequestFormModal';
import logo from '../../assets/image/logo.png';

function CreditControl() {
  const buyLink = "https://docs.google.com/forms/d/1ZPjtH69wDXelkXV_mQVM1CEHRcuSWF1mdDZANegsMlA/edit";
  const [isScrolled, setIsScrolled] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isRequestFormOpen, setIsRequestFormOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Payment configuration
  const paymentConfig = {
    amount: 1500, // Amount in INR
    productName: "8-Week Credit Control & Payment Recovery Coaching"
  };

  const openPaymentModal = () => {
    setIsPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setIsPaymentModalOpen(false);
  };

  const openRequestForm = () => {
    setIsRequestFormOpen(true);
  };

  const closeRequestForm = () => {
    setIsRequestFormOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 text-white scroll-smooth">
      {/* Custom Styles for Animations */}
      <style>
        {`
          @keyframes gradient-shift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          .animate-gradient-shift {
            background-size: 200% 200%;
            animation: gradient-shift 8s ease infinite;
          }
          @keyframes pulse-slow {
            0%, 100% { transform: scale(1); opacity: 0.3; }
            50% { transform: scale(1.2); opacity: 0.6; }
          }
          .animate-pulse-slow {
            animation: pulse-slow 15s ease-in-out infinite;
          }
        `}
      </style>

      {/* Sticky Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-slate-900/95 backdrop-blur-xl border-b border-amber-500/20 py-3 shadow-lg' 
          : 'bg-transparent py-4'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-4 group">
            <div className="relative">
              <div className={`absolute -inset-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl blur opacity-0 group-hover:opacity-50 transition-opacity duration-500 ${isScrolled ? '' : 'group-hover:opacity-30'}`}></div>
              <div className="relative w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-2xl p-1.5 shadow-lg">
                <img 
                  src={logo} 
                  alt="Rushneek Singh Logo" 
                  className="w-full h-full rounded-xl object-contain"
                />
              </div>
            </div>
            <div className="hidden sm:block">
              <span className="text-xl sm:text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-500 group-hover:from-amber-400 group-hover:to-orange-500 transition-all duration-300">
                Rushneek Singh
              </span>
              <span className="block text-xs sm:text-sm text-slate-400 font-medium">
                India's No. 1 B2B Payment Recovery Coach
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button 
              onClick={openRequestForm}
              className="group/btn relative inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 via-yellow-500 to-orange-500 text-slate-900 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full font-bold text-sm shadow-lg shadow-amber-500/25 hover:shadow-amber-500/50 hover:scale-105 transition-all duration-300 overflow-hidden"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700"></span>
              <span className="relative flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-slate-900/50 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-slate-900"></span>
                </span>
                Book a call
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <HeroSection buyLink={buyLink} onBuyClick={openRequestForm} />
        <VideoSection />
        <AchievementsSection buyLink={buyLink} onBuyClick={openRequestForm} />
        <FrameworkSection buyLink={buyLink} onBuyClick={openRequestForm} />
        <CreditControlSystemSection buyLink={buyLink} onBuyClick={openRequestForm} />
        <ClientAssessmentSection buyLink={buyLink} onBuyClick={openRequestForm} />
        <CashFlowSection buyLink={buyLink} onBuyClick={openRequestForm} />
        <CoachingPlanSection buyLink={buyLink} onBuyClick={openRequestForm} />
        <ResultsSection buyLink={buyLink} onBuyClick={openRequestForm} />
        <VideoTestimonialsSection buyLink={buyLink} onBuyClick={openRequestForm} />
        <TestimonialsSection />
        <ContactSection buyLink={buyLink} onBuyClick={openRequestForm} />
        {/* <FooterSection /> */}
      </main>

      {/* Payment Modal */}
      <PaymentModal 
        isOpen={isPaymentModalOpen}
        onClose={closePaymentModal}
        amount={paymentConfig.amount}
        productName={paymentConfig.productName}
      />

      {/* Request Form Modal */}
      <RequestFormModal 
        isOpen={isRequestFormOpen}
        onClose={closeRequestForm}
      />

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-24 md:bottom-8 right-6 z-40 w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:scale-110 transition-all duration-300 ${
          isScrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
        }`}
        aria-label="Back to top"
      >
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
}

export default CreditControl;
